import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TippyDirective } from '@/directives/tippy.directive';
import {
  onChainBidContractTippyOptions,
  showOnChainBidContractTooltip,
} from '@/utils/native-market-bid-labels';

export type OnChainBidInfoIconColorMode = 'contrast' | 'surface';

/** Material info icon with punksmarket.app contract tooltip (V1 on-chain bids). */
@Component({
  selector: 'app-on-chain-bid-info-icon',
  standalone: true,
  imports: [CommonModule, TippyDirective],
  template: `
    <span
      *ngIf="visible"
      class="on-chain-bid-info-icon"
      [class.on-chain-bid-info-icon--contrast]="!insetInPill && colorMode === 'contrast'"
      [class.on-chain-bid-info-icon--surface]="!insetInPill && colorMode === 'surface'"
      [class.on-chain-bid-info-icon--in-pill]="insetInPill"
      [style.width.px]="iconSize"
      [style.height.px]="iconSize"
      tippy
      [options]="tippyOptions"
      (click)="onIconClick($event)"
      aria-label="On-chain bid contract info">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        [attr.height]="iconSize"
        [attr.width]="iconSize"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
      </svg>
    </span>
  `,
  styles: [
    `
      .on-chain-bid-info-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        vertical-align: middle;
        flex-shrink: 0;
        cursor: pointer !important;
        line-height: 0;
        box-sizing: content-box;
      }

      .on-chain-bid-info-icon--contrast {
        color: rgba(var(--background), 0.85);
      }

      .on-chain-bid-info-icon--contrast:hover {
        color: rgba(var(--background), 1);
      }

      .on-chain-bid-info-icon--surface {
        color: rgba(var(--text-color), 0.45);
      }

      .on-chain-bid-info-icon--surface:hover {
        color: rgba(var(--text-color), 0.65);
      }

      .on-chain-bid-info-icon--in-pill {
        color: rgba(var(--wrapped-color), 1) !important;
      }

      .on-chain-bid-info-icon--in-pill:hover {
        color: rgba(var(--wrapped-color), 0.75) !important;
      }

      .on-chain-bid-info-icon svg {
        display: block;
      }
    `,
  ],
})
export class OnChainBidInfoIconComponent {
  @Input() show = showOnChainBidContractTooltip;
  @Input() iconSize: 14 | 16 = 16;
  /** `contrast` on colored buttons; `surface` next to normal text. */
  @Input() colorMode: OnChainBidInfoIconColorMode = 'surface';
  /** Tighter offset when rendered inside a bid-type pill. */
  @Input() insetInPill = false;

  readonly tippyOptions = onChainBidContractTippyOptions;

  get visible(): boolean {
    return this.show && !!this.tippyOptions?.content;
  }

  onIconClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }
}
